import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Pagination,
  IconButton,
  Snackbar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Store as StoreIcon,
  LocalOffer as TagIcon,
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  MoreVert as MoreIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  Info as InfoIcon,
  Star as StarIcon,
  NewReleases as NewReleasesIcon,
  NotificationsActive as NotificationIcon,
  ArrowForward as ArrowForwardIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
  Check as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { marketplaceService, tokenWalletService } from '../../../services/skillcoinService';
import { format } from 'date-fns';
import RedeemItemModal from './RedeemItemModal';
import CheckoutSystem from './CheckoutSystem';
import { motion } from 'framer-motion';

const MarketplaceBrowser = () => {
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [sortBy, setSortBy] = useState('recent');
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState({ totalItems: 0, totalPages: 1, currentPage: 1 });
  const [tokenSymbol, setTokenSymbol] = useState('SKC');
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [purchaseData, setPurchaseData] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  
  // Enhanced states for enterprise checkout
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [orderTracking, setOrderTracking] = useState({});
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    fetchItems();
    fetchWalletBalance();
    fetchTransactionHistory();
    fetchUserProfile();
  }, []);

  useEffect(() => {
    // Apply filters and search
    let result = [...items];

    // Search term
    if (searchTerm) {
      result = result.filter(
        item =>
          item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      result = result.filter(item => item.categories && item.categories.includes(categoryFilter));
    }

    // Price range
    result = result.filter(
      item => item.price >= priceRange[0] && item.price <= priceRange[1]
    );

    // Sort
    if (sortBy === 'recent') {
      result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortBy === 'price_low') {
      result.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price_high') {
      result.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'popularity') {
      result.sort((a, b) => (b.purchaseCount || 0) - (a.purchaseCount || 0));
    }

    setFilteredItems(result);
    setTotalPages(Math.ceil(result.length / 8)); // 8 items per page
  }, [items, searchTerm, categoryFilter, priceRange, sortBy]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      console.log('Fetching marketplace items with auth token:', !!localStorage.getItem('authToken'));
      
      let response;
      try {
        response = await marketplaceService.getItems({ page: 1, limit: 100, status: 'active' });
        console.log('Marketplace response:', response);
      } catch (error) {
        console.warn('Error using marketplaceService.getItems, trying fallback method', error);
        
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenantId');
        
        if (!authToken || !tenantId) {
          throw new Error('Missing authentication credentials');
        }
        
        const resp = await fetch(`${apiUrl}/v1/skillcoin/marketplace`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'x-tenant-id': tenantId
          }
        });
        
        if (!resp.ok) {
          throw new Error(`API error: ${resp.status}`);
        }
        
        const data = await resp.json();
        console.log('Direct API marketplace response:', data);
        
        if (data.success && data.data) {
          response = {
            listings: data.data,
            pagination: data.pagination || { totalItems: data.data.length }
          };
        } else if (Array.isArray(data)) {
          response = {
            listings: data,
            pagination: { totalItems: data.length }
          };
        } else {
          response = {
            listings: [],
            pagination: { totalItems: 0 }
          };
        }
      }
      
      let items = [];
      let pagination = { totalItems: 0, totalPages: 1, currentPage: 1 };
      
      if (response && response.listings) {
        items = response.listings;
        pagination = response.pagination || pagination;
      } else if (response && response.data) {
        items = response.data;
        pagination = response.pagination || { totalItems: items.length };
      } else if (Array.isArray(response)) {
        items = response;
        pagination = { totalItems: response.length };
      } else {
        console.warn('Unexpected response format:', response);
        items = [];
      }
      
      console.log('Processed marketplace items:', items);
      setItems(items);
      setPagination(pagination);
      setError(null);
    } catch (error) {
      console.error('Error loading marketplace items:', error);
      setError('Failed to load marketplace items. Please try again later.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      console.log('Fetching wallet balance');
      
      let walletData;
      try {
        // Try using the tokenWalletService
        const wallets = await tokenWalletService.getWallets();
        console.log('Wallet response:', wallets);
        
        if (wallets && wallets.length > 0) {
          walletData = wallets[0];
        } else if (wallets && wallets.data && wallets.data.length > 0) {
          walletData = wallets.data[0];
        }
      } catch (serviceError) {
        console.warn('Error using tokenWalletService, trying direct API call', serviceError);
        
        // Direct API call as fallback
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenantId');
        
        if (!authToken || !tenantId) {
          throw new Error('Missing authentication credentials');
        }
        
        const response = await fetch(`${apiUrl}/v1/skillcoin/wallets`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'x-tenant-id': tenantId
          }
        });
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Direct API wallet response:', data);
        
        if (data.success && data.data && data.data.length > 0) {
          walletData = data.data[0];
        } else if (Array.isArray(data) && data.length > 0) {
          walletData = data[0];
        }
      }
      
      console.log('Processed wallet data:', walletData);
      
      if (walletData) {
        setWalletBalance(walletData.balance || 0);
        const symbol = walletData.token?.symbol || walletData.tokenId?.symbol || 'SKC';
        setTokenSymbol(symbol);
        console.log(`Updated wallet balance: ${walletData.balance} ${symbol}`);
      } else {
        setWalletBalance(0);
        setTokenSymbol('SKC');
        console.log('No wallet data found, using defaults');
      }
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
      setWalletBalance(0);
      setTokenSymbol('SKC');
    }
  };

  const fetchTransactionHistory = async () => {
    try {
      // Use the correct API method from marketplaceService
      const response = await marketplaceService.getPurchaseHistory();
      console.log('Transaction history response:', response);
      
      // Process the response based on its structure
      let transactions = [];
      
      if (response && response.success && response.data) {
        transactions = response.data;
      } else if (Array.isArray(response)) {
        transactions = response;
      } else if (response && Array.isArray(response.data)) {
        transactions = response.data;
      }
      
      console.log('Processed transaction history:', transactions);
      setTransactionHistory(transactions);
      
      // Set notification count based on new transactions (example logic)
      const unreadCount = transactions.filter(t => !t.viewed).length;
      setNotificationCount(unreadCount);
      
      // Set recent purchases for quick view
      setRecentPurchases(transactions.slice(0, 3));
      
      // Generate mock order tracking data
      const mockTracking = {};
      transactions.forEach(t => {
        if (t._id) {
          mockTracking[t._id] = {
            status: Math.random() > 0.7 ? 'delivered' : Math.random() > 0.5 ? 'shipped' : 'processing',
            estimatedDelivery: new Date(Date.now() + Math.random() * 7 * 24 * 60 * 60 * 1000),
            trackingNumber: `TRK${Math.floor(1000000 + Math.random() * 9000000)}`
          };
        }
      });
      setOrderTracking(mockTracking);
    } catch (error) {
      console.error('Error fetching transaction history:', error);
      
      // Fallback to direct API call if the service method fails
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const authToken = localStorage.getItem('authToken') || localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenantId');
        
        if (!authToken || !tenantId) {
          throw new Error('Missing authentication credentials');
        }
        
        const response = await fetch(`${apiUrl}/v1/skillcoin/marketplace/purchases`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'x-tenant-id': tenantId
          }
        });
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Direct API transaction history response:', data);
        
        let transactions = [];
        if (data && data.success && data.data) {
          transactions = data.data;
        } else if (Array.isArray(data)) {
          transactions = data;
        } else if (data && Array.isArray(data.data)) {
          transactions = data.data;
        }
        
        setTransactionHistory(transactions);
      } catch (fallbackError) {
        console.error('Fallback transaction history fetch failed:', fallbackError);
        setTransactionHistory([]);
      }
    }
  };

  const fetchUserProfile = async () => {
    try {
      // This would normally be an API call to get user profile
      // Mock implementation for example
      const mockProfile = {
        id: '12345',
        firstName: 'John',
        lastName: 'Doe',
        email: 'john.doe@example.com',
        avatar: null,
        preferences: {
          notifications: true,
          currency: 'USD'
        }
      };
      
      setUserProfile(mockProfile);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleCategoryChange = (event) => {
    setCategoryFilter(event.target.value);
    setPage(1);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handlePriceRangeChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setItemDialogOpen(true);
  };

  const handleItemDialogClose = () => {
    setItemDialogOpen(false);
  };

  const handleCheckoutClick = () => {
    setItemDialogOpen(false);
    setCheckoutOpen(true);
    setPurchaseError(null);
    setPurchaseSuccess(false);
  };

  const handleCheckoutClose = () => {
    setCheckoutOpen(false);
  };

  const handleCheckoutSubmit = async (checkoutData) => {
    if (!selectedItem) return;

    try {
      console.log('Processing order with checkout data:', checkoutData);
      
      // Extract quantity from checkout data
      const { quantity } = checkoutData;
      
      // Validate quantity is a positive integer
      if (!Number.isInteger(quantity) || quantity <= 0) {
        throw new Error('Quantity must be a positive integer');
      }
      
      // Make the API call to purchase the item
      const response = await marketplaceService.purchaseItem(selectedItem._id, quantity);
      console.log('Purchase response:', response);
      
      let successData;
      
      if (response && response.success && response.data) {
        successData = response.data;
      } else if (response && response.transaction) {
        successData = response;
      } else {
        successData = { 
          message: 'Purchase completed successfully',
          orderNumber: checkoutData.orderNumber,
          timestamp: checkoutData.timestamp
        };
      }
      
      // Update cart/transaction state
      const newPurchase = {
        ...successData,
        item: selectedItem,
        quantity: checkoutData.quantity,
        orderNumber: checkoutData.orderNumber || `ORD-${Math.floor(100000 + Math.random() * 900000)}`,
        date: new Date().toISOString(),
        status: 'completed',
        shippingDetails: checkoutData.shippingDetails,
        deliveryMethod: checkoutData.deliveryMethod
      };
      
      // Update recent purchases list
      setRecentPurchases([newPurchase, ...recentPurchases.slice(0, 2)]);
      
      // Update order tracking data
      const newTracking = { ...orderTracking };
      newTracking[newPurchase.orderNumber] = {
        status: 'processing',
        estimatedDelivery: new Date(Date.now() + (selectedItem.isPhysical ? 5 : 1) * 24 * 60 * 60 * 1000),
        trackingNumber: `TRK${Math.floor(1000000 + Math.random() * 9000000)}`
      };
      setOrderTracking(newTracking);
      
      // Refresh wallet balance after successful purchase
      fetchWalletBalance();
      
      // Refresh marketplace listings to update item quantities
      fetchItems();
      
      // Refresh transaction history
      fetchTransactionHistory();
      
      // Set purchase success state
      setPurchaseComplete(true);
      setPurchaseError(null);
      setPurchaseData(successData);
      
      // Show success message
      setSnackbarMessage(`Your order #${checkoutData.orderNumber} has been completed successfully!`);
      setSnackbarOpen(true);
      
      return successData;
    } catch (error) {
      console.error('Error purchasing item:', error);
      
      // Check if the error is due to item no longer being available
      const errorMessage = error.message || 'Failed to purchase item. Please try again.';
      setPurchaseError(errorMessage);
      
      // If item is no longer available, refresh marketplace listings to update availability
      if (errorMessage.toLowerCase().includes('no longer available')) {
        fetchItems();
      }
      
      throw error;
    }
  };

  const toggleFavorite = (itemId) => {
    if (favoriteItems.includes(itemId)) {
      setFavoriteItems(favoriteItems.filter(id => id !== itemId));
    } else {
      setFavoriteItems([...favoriteItems, itemId]);
    }
  };

  const getItemsForCurrentPage = () => {
    const startIndex = (page - 1) * 8;
    const endIndex = startIndex + 8;
    return filteredItems.slice(startIndex, endIndex);
  };

  const getListingTypeLabel = (type) => {
    const types = {
      'physical_item': 'Physical Item',
      'digital_content': 'Digital Content',
      'service': 'Service',
      'event': 'Event',
      'consultation': 'Consultation',
      'other': 'Other'
    };
    return types[type] || type;
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Add item to cart feature (placeholder for future implementation)
  const addToCart = (item) => {
    setCartItems([...cartItems, { ...item, quantity: 1 }]);
    setSnackbarMessage(`${item.title || item.name} added to cart`);
    setSnackbarOpen(true);
  };

  if (loading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && items.length === 0) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3
      }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
          <StoreIcon sx={{ mr: 1 }} />
          Skillcoin Marketplace
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Your wallet balance">
            <Chip
              icon={<TagIcon />}
              label={`${walletBalance} ${tokenSymbol}`}
              color="primary"
              variant="outlined"
              sx={{ mr: 2, fontWeight: 'bold' }}
            />
          </Tooltip>
          
          <Tooltip title="Notifications">
            <IconButton color="primary" sx={{ mr: 1 }}>
              <Badge badgeContent={notificationCount} color="error">
                <NotificationIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Tabs 
        value={activeTab} 
        onChange={handleTabChange} 
        sx={{ 
          mb: 3, 
          '& .MuiTab-root': { 
            fontSize: '1rem', 
            fontWeight: 'medium',
            py: 2 
          },
          '& .Mui-selected': {
            fontWeight: 'bold',
            color: 'primary.main'
          }
        }}
        variant="fullWidth"
      >
        <Tab 
          label="Marketplace" 
          icon={<StoreIcon />} 
          iconPosition="start"
        />
        <Tab 
          label="Transaction History" 
          icon={<HistoryIcon />} 
          iconPosition="start"
          sx={{
            animation: purchaseComplete ? 'pulse 1.5s ease-in-out' : 'none',
            '@keyframes pulse': {
              '0%': { bgcolor: 'transparent' },
              '50%': { bgcolor: 'rgba(144, 202, 249, 0.3)' },
              '100%': { bgcolor: 'transparent' }
            }
          }}
        />
      </Tabs>

      {activeTab === 0 && (
        <>
          {/* Filters and Search */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="category-filter-label">Category</InputLabel>
                  <Select
                    labelId="category-filter-label"
                    value={categoryFilter}
                    onChange={handleCategoryChange}
                    label="Category"
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    <MenuItem value="services">Services</MenuItem>
                    <MenuItem value="digital">Digital Products</MenuItem>
                    <MenuItem value="physical">Physical Products</MenuItem>
                    <MenuItem value="events">Events</MenuItem>
                    <MenuItem value="education">Education</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="sort-by-label">Sort By</InputLabel>
                  <Select
                    labelId="sort-by-label"
                    value={sortBy}
                    onChange={handleSortChange}
                    label="Sort By"
                  >
                    <MenuItem value="recent">Most Recent</MenuItem>
                    <MenuItem value="price_low">Price: Low to High</MenuItem>
                    <MenuItem value="price_high">Price: High to Low</MenuItem>
                    <MenuItem value="popularity">Popularity</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Chip
                    icon={<CartIcon />}
                    label={`Cart (${cartItems.length})`}
                    color={cartItems.length > 0 ? "primary" : "default"}
                    variant={cartItems.length > 0 ? "filled" : "outlined"}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Recent Orders Summary (New Feature) */}
          {recentPurchases.length > 0 && (
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.03) }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon sx={{ mr: 1, fontSize: 20 }} color="primary" />
                Recent Orders
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, overflow: 'auto', pb: 1 }}>
                {recentPurchases.map((purchase, index) => (
                  <Paper 
                    key={index}
                    variant="outlined"
                    sx={{ 
                      minWidth: 220,
                      p: 1.5,
                      borderRadius: 1,
                      borderColor: theme.palette.divider,
                      '&:hover': { borderColor: theme.palette.primary.main }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Chip 
                        label={orderTracking[purchase.orderNumber]?.status || "processing"} 
                        size="small"
                        color={
                          orderTracking[purchase.orderNumber]?.status === 'delivered' ? 'success' :
                          orderTracking[purchase.orderNumber]?.status === 'shipped' ? 'info' : 'warning'
                        }
                        icon={
                          orderTracking[purchase.orderNumber]?.status === 'delivered' ? <CheckIcon /> :
                          orderTracking[purchase.orderNumber]?.status === 'shipped' ? <LocationIcon /> : <WarningIcon />
                        }
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(purchase.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" noWrap fontWeight="medium">
                      {purchase.item?.title || purchase.item?.name || "Item Purchase"}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary" display="block">
                      Order #{purchase.orderNumber}
                    </Typography>
                    
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {purchase.amount || purchase.item?.price || 0} {tokenSymbol}
                      </Typography>
                      <ArrowForwardIcon fontSize="small" color="action" />
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Paper>
          )}

          {/* Item Grid */}
          {filteredItems.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                No items found
              </Typography>
              <Typography variant="body1">
                Try adjusting your search or filters to find what you're looking for.
              </Typography>
            </Paper>
          ) : (
            <>
              <Grid container spacing={3}>
                {getItemsForCurrentPage().map((item) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)'
                        }
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="160"
                          image={item.images && item.images.length > 0 ? item.images[0].url : 'https://via.placeholder.com/300x160?text=No+Image'}
                          alt={item.title}
                          sx={{ objectFit: 'cover' }}
                        />
                        
                        {/* Item badges - new feature */}
                        <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
                          {item.createdAt && new Date(item.createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) && (
                            <Chip 
                              size="small" 
                              label="New" 
                              color="error" 
                              sx={{ 
                                mb: 1, 
                                fontWeight: 'bold', 
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)' 
                              }} 
                              icon={<NewReleasesIcon />} 
                            />
                          )}
                        </Box>
                        
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(item._id);
                          }}
                          sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'rgba(255,255,255,0.8)' }}
                        >
                          {favoriteItems.includes(item._id) ? (
                            <FavoriteIcon color="error" />
                          ) : (
                            <FavoriteBorderIcon />
                          )}
                        </IconButton>
                      </Box>
                      
                      <CardContent sx={{ flexGrow: 1, pt: 2 }}>
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            label={getListingTypeLabel(item.listingType)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                        
                        <Typography variant="h6" component="div" gutterBottom noWrap>
                          {item.title}
                        </Typography>
                        
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {item.description}
                        </Typography>
                        
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                          {item.price} {tokenSymbol}
                        </Typography>
                        
                        {item.quantity !== undefined && item.quantity <= 5 && (
                          <Typography variant="caption" color="error.main">
                            Only {item.quantity} left in stock!
                          </Typography>
                        )}
                      </CardContent>
                      
                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => handleItemClick(item)}
                          startIcon={<CartIcon />}
                        >
                          View Details
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            </>
          )}

          {/* Item Detail Dialog */}
          <Dialog
            open={itemDialogOpen}
            onClose={handleItemDialogClose}
            maxWidth="md"
            fullWidth
          >
            {selectedItem && (
              <>
                <DialogTitle>{selectedItem.title}</DialogTitle>
                <DialogContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <img
                        src={selectedItem.images && selectedItem.images.length > 0 ? selectedItem.images[0].url : 'https://via.placeholder.com/600x400?text=No+Image'}
                        alt={selectedItem.title}
                        style={{ width: '100%', borderRadius: '8px' }}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/600x400?text=Image+Not+Available';
                        }}
                      />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                        {selectedItem.categories && selectedItem.categories.map((category, index) => (
                          <Chip key={index} label={category} size="small" />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>
                        Description
                      </Typography>
                      <Typography variant="body1" paragraph>
                        {selectedItem.description}
                      </Typography>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
                        {selectedItem.price} {tokenSymbol}
                      </Typography>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Listing Type:
                      </Typography>
                      <Typography variant="body1" paragraph>
                        {getListingTypeLabel(selectedItem.listingType)}
                      </Typography>
                      
                      {selectedItem.estimatedDeliveryTime && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Estimated Delivery:
                          </Typography>
                          <Typography variant="body1" paragraph>
                            {selectedItem.estimatedDeliveryTime} hours
                          </Typography>
                        </>
                      )}
                      
                      {selectedItem.quantity !== undefined && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Available:
                          </Typography>
                          <Typography variant="body1" paragraph>
                            {selectedItem.quantity} {selectedItem.quantity === 1 ? 'item' : 'items'}
                          </Typography>
                        </>
                      )}
                      
                      <Alert
                        severity={walletBalance >= selectedItem.price ? 'info' : 'warning'}
                        sx={{ mb: 2 }}
                      >
                        {walletBalance >= selectedItem.price
                          ? `You have enough tokens! Your balance: ${walletBalance} ${tokenSymbol}`
                          : `Insufficient tokens. You need ${selectedItem.price - walletBalance} more ${tokenSymbol}.`}
                      </Alert>
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleItemDialogClose}>Close</Button>
                  <Button
                    variant="contained"
                    startIcon={<CartIcon />}
                    disabled={walletBalance < selectedItem.price}
                    onClick={handleCheckoutClick}
                  >
                    Checkout
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* Enterprise Checkout System */}
          {selectedItem && (
            <CheckoutSystem
              open={checkoutOpen}
              onClose={handleCheckoutClose}
              item={{
                _id: selectedItem._id || 'item-id',
                name: selectedItem.title || selectedItem.name || 'Item',
                tokenPrice: selectedItem.price || 0,
                quantity: selectedItem.quantity || 10,
                isPhysical: selectedItem.listingType === 'physical_item',
                description: selectedItem.description || 'No description available',
                imageUrl: selectedItem.images && selectedItem.images.length > 0 ? selectedItem.images[0].url : null,
                category: selectedItem.categories && selectedItem.categories.length > 0 ? selectedItem.categories[0] : null
              }}
              onSubmit={handleCheckoutSubmit}
              walletBalance={walletBalance || 0}
              userProfile={userProfile}
            />
          )}

          {/* Snackbar for success message */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            message={snackbarMessage}
          />
        </>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3, boxShadow: 3, borderRadius: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 3,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h5" fontWeight="bold" sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: 'primary.main'
            }}>
              <HistoryIcon sx={{ mr: 1, fontSize: 28 }} />
              Transaction History
            </Typography>
            <Button
              variant="outlined"
              onClick={fetchTransactionHistory}
              startIcon={<HistoryIcon />}
              size="small"
            >
              Refresh
            </Button>
          </Box>
          
          {transactionHistory.length === 0 ? (
            <Box sx={{ 
              p: 4, 
              textAlign: 'center',
              bgcolor: 'background.default',
              borderRadius: 1,
              my: 2 
            }}>
              <img 
                src="https://via.placeholder.com/150?text=No+History" 
                alt="No History" 
                style={{ width: 120, height: 120, marginBottom: 16, opacity: 0.7 }} 
              />
              <Typography variant="h6" fontWeight="medium" color="text.secondary" gutterBottom>
                No Purchase History Found
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Once you make purchases in the marketplace, they will appear here.
              </Typography>
              <Button
                variant="contained"
                onClick={() => setActiveTab(0)}
                startIcon={<StoreIcon />}
                color="primary"
                sx={{ mt: 1 }}
              >
                Go to Marketplace
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  Your recent purchase history is displayed below. You can view details of each transaction.
                </Typography>
              </Box>
              <List sx={{ 
                bgcolor: 'background.paper', 
                borderRadius: 1,
                boxShadow: 1
              }}>
                {transactionHistory.map((transaction, index) => {
                  // Handle different transaction data structures
                  const itemName = transaction.description || 
                                   transaction.metadata?.itemName || 
                                   (transaction.relatedItemId && transaction.relatedItemId.title) || 
                                   'Item Purchase';
                  
                  const amount = transaction.amount || transaction.metadata?.totalCost || 0;
                  const date = transaction.date || transaction.createdAt || new Date();
                  const quantity = transaction.metadata?.quantity || 1;
                  const transactionId = transaction._id || transaction.reference || `transaction-${index}`;
                  
                  return (
                    <ListItem 
                      key={transactionId}
                      divider={index < transactionHistory.length - 1}
                      sx={{ 
                        py: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': { 
                          bgcolor: 'rgba(0, 0, 0, 0.04)',
                          transform: 'translateY(-2px)',
                          boxShadow: 1
                        } 
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: 'primary.main',
                          boxShadow: 2,
                          p: 0.5
                        }}>
                          <ReceiptIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" fontWeight="bold">
                            {itemName}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              display="block"
                              sx={{ mt: 0.5 }}
                            >
                              <span style={{ fontWeight: 'medium' }}>Transaction Date:</span> {new Date(date).toLocaleString()}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              display="block"
                              sx={{ mt: 0.5 }}
                            >
                              <span style={{ fontWeight: 'medium' }}>Quantity:</span> {quantity}
                            </Typography>
                            {transaction.reference && (
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                display="block"
                                sx={{ mt: 0.5 }}
                              >
                                <span style={{ fontWeight: 'medium' }}>Reference:</span> {transaction.reference}
                              </Typography>
                            )}
                            
                            {/* Order Tracking Information (new feature) */}
                            {orderTracking[transactionId] && (
                              <Box sx={{ mt: 1 }}>
                                <Chip 
                                  size="small"
                                  label={orderTracking[transactionId].status.toUpperCase()}
                                  color={
                                    orderTracking[transactionId].status === 'delivered' ? 'success' :
                                    orderTracking[transactionId].status === 'shipped' ? 'info' : 'warning'
                                  }
                                  sx={{ mr: 1, mb: 1 }}
                                />
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Tracking #: {orderTracking[transactionId].trackingNumber}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Est. Delivery: {new Date(orderTracking[transactionId].estimatedDelivery).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}
                          </>
                        }
                      />
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'flex-end' 
                      }}>
                        <Typography 
                          variant="h6" 
                          color="error.main" 
                          fontWeight="bold"
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center' 
                          }}
                        >
                          {amount} {tokenSymbol}
                        </Typography>
                        <Chip 
                          label="Purchase" 
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default MarketplaceBrowser;
